import { useEffect, useRef, useState } from "react";
import { Pencil, Eraser, FlipHorizontal, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export const N = 60;
export const EMPTY_GLYPH = "0".repeat(N * N);

export function GlyphView({ pixels, size = 32, className = "", testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, N, N);
    ctx.fillStyle = "#000000";
    for (let i = 0; i < pixels.length; i++) if (pixels[i] === "1") ctx.fillRect(i % N, Math.floor(i / N), 1, 1);
  }, [pixels]);
  return <canvas ref={ref} width={N} height={N} className={`rounded-sm [image-rendering:pixelated] ${className}`} style={{ width: size, height: size }} data-testid={testid} />;
}

const Tool = ({ active, onClick, children, testid, title }) => (
  <Button type="button" size="sm" variant={active ? "default" : "outline"} onClick={onClick} title={title} data-testid={testid}>{children}</Button>
);

export function GlyphCanvas({ pixels, onChange }) {
  const [tool, setTool] = useState("pencil");
  const [brush, setBrush] = useState(1);
  const drawing = useRef(false);
  const buf = useRef(pixels.split(""));
  const ref = useRef(null);
  useEffect(() => { buf.current = pixels.split(""); }, [pixels]);

  const paint = (e) => {
    const r = ref.current.getBoundingClientRect();
    const x = Math.floor(((e.clientX - r.left) / r.width) * N), y = Math.floor(((e.clientY - r.top) / r.height) * N);
    const v = tool === "pencil" ? "1" : "0";
    let changed = false;
    for (let dy = 0; dy < brush; dy++) for (let dx = 0; dx < brush; dx++) {
      const px = x + dx - Math.floor(brush / 2), py = y + dy - Math.floor(brush / 2);
      if (px >= 0 && py >= 0 && px < N && py < N && buf.current[py * N + px] !== v) { buf.current[py * N + px] = v; changed = true; }
    }
    if (changed) onChange(buf.current.join(""));
  };
  const mirror = () => {
    const out = [];
    for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) out.push(pixels[y * N + (N - 1 - x)]);
    onChange(out.join(""));
  };
  return (
    <div className="space-y-3" data-testid="glyph-canvas">
      <div className="flex flex-wrap items-center gap-1.5">
        <Tool active={tool === "pencil"} onClick={() => setTool("pencil")} testid="glyph-tool-pencil" title="Pencil"><Pencil className="h-3.5 w-3.5 mr-1" />Pencil</Tool>
        <Tool active={tool === "eraser"} onClick={() => setTool("eraser")} testid="glyph-tool-eraser" title="Eraser"><Eraser className="h-3.5 w-3.5 mr-1" />Eraser</Tool>
        <Tool onClick={mirror} testid="glyph-tool-mirror" title="Mirror left-right"><FlipHorizontal className="h-3.5 w-3.5 mr-1" />Mirror</Tool>
        <Tool onClick={() => onChange(EMPTY_GLYPH)} testid="glyph-tool-clear" title="Clear"><Trash2 className="h-3.5 w-3.5 mr-1" />Clear</Tool>
        <span className="ml-2 text-xs text-[#A8A090]">Brush</span>
        {[1, 2, 3].map((b) => <Tool key={b} active={brush === b} onClick={() => setBrush(b)} testid={`glyph-brush-${b}`}>{b}</Tool>)}
      </div>
      <div ref={ref} className="relative w-full max-w-[360px] aspect-square cursor-crosshair touch-none select-none border border-[#2D3442]"
        onPointerDown={(e) => { drawing.current = true; e.currentTarget.setPointerCapture(e.pointerId); paint(e); }}
        onPointerMove={(e) => drawing.current && paint(e)} onPointerUp={() => { drawing.current = false; }} data-testid="glyph-draw-area">
        <GlyphView pixels={pixels} size="100%" className="absolute inset-0 !w-full !h-full rounded-none" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)", backgroundSize: `${100 / 12}% ${100 / 12}%` }} />
      </div>
    </div>
  );
}
